/**
 * DiffusionSuiviPage — qui a répondu, qui reste silencieux (mig 254).
 *
 * L'écran qui rend une campagne DUERP exploitable : sans lui, l'expéditeur sait
 * qu'il a envoyé, pas si ça a été reçu. Et la relance ne vise QUE les
 * silencieux — renvoyer à toute la liste, c'est le meilleur moyen d'apprendre
 * aux agents à ignorer les rappels.
 */

import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Alert, Button, Badge, ModuleHeader } from '@/components/ui';
import { AppHeader } from '@/components/AppHeader';
import { api, ApiException } from '@/lib/api';
import type { SuiviDiffusion } from '@roronoa-collectivites/shared-types';
import { Megaphone, BellRing, CheckCheck, Loader2, ArrowLeft } from 'lucide-react';

export function DiffusionSuiviPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [suivi, setSuivi] = useState<SuiviDiffusion | null>(null);
  const [loading, setLoading] = useState(true);
  const [relance, setRelance] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const charger = useCallback(async () => {
    if (!id) return;
    setLoading(true); setError(null);
    try { setSuivi(await api.diffusions.suivi(id)); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Chargement impossible'); }
    finally { setLoading(false); }
  }, [id]);
  useEffect(() => { void charger(); }, [charger]);

  async function relancerLesSilencieux() {
    if (!id) return;
    setRelance(true); setError(null); setMsg(null);
    try {
      const r = await api.diffusions.relancer(id);
      setMsg(r.relances === 0
        ? 'Tout le monde a répondu — personne à relancer.'
        : `${r.relances} agent${r.relances > 1 ? 's' : ''} relancé${r.relances > 1 ? 's' : ''}.`);
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Relance impossible');
    } finally { setRelance(false); }
  }

  const pct = suivi && suivi.attendus > 0
    ? Math.round((suivi.acquittes / suivi.attendus) * 100)
    : 0;

  return (
    <div className="min-h-screen text-ink">
      {/* Le bandeau mairie manquait : sans lui, aucun retour au tableau de bord
          depuis ces écrans. Remonté par la mairie le 28/07. */}
      <AppHeader module="Messagerie interne" accent="grc" />
      <ModuleHeader titreNiveau="div" accent="grc" icon={Megaphone} eyebrow="Diffusion interne" title="Suivi des réponses" />

      <main id="main-content" className="mx-auto max-w-3xl px-4 py-6 sm:px-6 sm:py-8">
        {error && <Alert variant="error" className="mb-4">{error}</Alert>}
        {msg && <Alert variant="success" className="mb-4">{msg}</Alert>}

        <Button variant="glass" size="sm" onClick={() => navigate('/grc/messagerie')} className="mb-4">
          <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Retour à la messagerie
        </Button>

        {loading ? (
          <div className="flex items-center gap-2 py-12 text-sm text-ink-muted">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Chargement…
          </div>
        ) : suivi ? (
          <>
            <Card variant="glass" padded>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <h1 className="font-display text-lg font-semibold tracking-r-tight text-ink">{suivi.objet}</h1>
                  {suivi.dateLimite && (
                    <p className="mt-0.5 text-xs text-ink-subtle">
                      Date limite : {new Intl.DateTimeFormat('fr-FR', {
                        day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Europe/Paris',
                      }).format(new Date(`${suivi.dateLimite}T12:00:00Z`))}
                    </p>
                  )}
                </div>
                <Badge variant="neutral" size="sm">
                  {suivi.retourAttendu === 'action_faite' ? 'Confirmation demandée' : 'Prise de connaissance'}
                </Badge>
              </div>

              <div className="mt-4 flex items-baseline gap-2">
                <span className="text-4xl font-semibold tabular-nums text-ink">{suivi.acquittes}</span>
                <span className="text-lg text-ink-muted">/ {suivi.attendus}</span>
                <span className="text-sm text-ink-subtle">ont répondu ({pct} %)</span>
              </div>

              <div className="mt-3 h-2 w-full overflow-hidden rounded-r-pill bg-surface">
                <div
                  className="h-full rounded-r-pill bg-grc transition-all"
                  style={{ width: `${pct}%` }}
                  role="progressbar"
                  aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}
                  aria-label="Part des agents ayant répondu"
                />
              </div>
            </Card>

            <Card variant="glass" padded className="mt-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h2 className="flex items-center gap-2 font-medium text-ink">
                  {suivi.enAttente.length === 0
                    ? <CheckCheck className="h-5 w-5 text-success" aria-hidden="true" />
                    : <BellRing className="h-5 w-5 text-urgent" aria-hidden="true" />}
                  {suivi.enAttente.length === 0
                    ? 'Tout le monde a répondu'
                    : `${suivi.enAttente.length} en attente`}
                </h2>
                {suivi.enAttente.length > 0 && (
                  <Button variant="grc" size="sm" loading={relance} onClick={() => void relancerLesSilencieux()}>
                    <BellRing className="h-4 w-4" aria-hidden="true" /> Relancer les silencieux
                  </Button>
                )}
              </div>

              {suivi.enAttente.length > 0 && (
                <ul className="mt-3 divide-y divide-border">
                  {suivi.enAttente.map((u) => (
                    <li key={u.userId} className="flex items-center justify-between py-2 text-sm">
                      <span className="text-ink">{u.nom || '—'}</span>
                      {u.service && <span className="text-xs text-ink-subtle">{u.service}</span>}
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </>
        ) : null}
      </main>
    </div>
  );
}
